import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchPlans,
  addPlans,
  deletePlans,
  fetchPlanDetails,
  editPlanDetails,
} from "../../services/services";

// Initial State
const initialState = {
  plansData: [],
  selectedPlan: null,
  isPlansLoading: false,
  isAddingPlan: false,
  isDeletingPlan: false,
  isFetchingPlanDetails: false,
  isError: false,
  isEditingPlan: false,
  errorMessage: "",
};

// Async Thunk for fetching all plans (GET)
export const fetchPlansAsync = createAsyncThunk(
  "addons/fetchAddons",
  async ({ page, limit, searchtext }, { rejectWithValue }) => {
    try {
      const response = await fetchPlans(page, limit, searchtext);
      return { data: response, page, limit, searchtext };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for fetching details of a specific plan (GET)
export const fetchPlanDetailsAsync = createAsyncThunk(
  "plans/fetchPlanDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchPlanDetails(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for adding a new plan (POST)
export const addPlanAsync = createAsyncThunk(
  "plans/addPlan",
  async (planData, { rejectWithValue }) => {
    try {
      const response = await addPlans(planData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for deleting a plan (DELETE)
export const deletePlanAsync = createAsyncThunk(
  "plans/deletePlan",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePlans(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for updating a plan (PUT)
export const editPlanAsync = createAsyncThunk(
  "plans/editPlan",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await editPlanDetails(id, updatedData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Plans Slice
const plansSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Plans
      .addCase(fetchPlansAsync.pending, (state) => {
        state.isPlansLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchPlansAsync.fulfilled, (state, action) => {
        state.isPlansLoading = false;
        state.plansData = action.payload;
      })
      .addCase(fetchPlansAsync.rejected, (state, action) => {
        state.isPlansLoading = false;
        state.isError = true;
        state.errorMessage = action.error.message;
      })

      // Fetch Plan Details
      .addCase(fetchPlanDetailsAsync.pending, (state) => {
        state.isFetchingPlanDetails = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchPlanDetailsAsync.fulfilled, (state, action) => {
        state.isFetchingPlanDetails = true;
        state.selectedPlan = action.payload;
      })
      .addCase(fetchPlanDetailsAsync.rejected, (state, action) => {
        state.isFetchingPlanDetails = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Add Plan
      .addCase(addPlanAsync.pending, (state) => {
        state.isAddingPlan = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(addPlanAsync.fulfilled, (state, action) => {
        state.isAddingPlan = true;
        state.plansData = action.payload;
      })
      .addCase(addPlanAsync.rejected, (state, action) => {
        state.isAddingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete Plan
      .addCase(deletePlanAsync.pending, (state) => {
        state.isDeletingPlan = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(deletePlanAsync.fulfilled, (state, action) => {
        state.isDeletingPlan = false;
      })
      .addCase(deletePlanAsync.rejected, (state, action) => {
        state.isDeletingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Edit Plan
      .addCase(editPlanAsync.pending, (state) => {
        state.isEditingPlan = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(editPlanAsync.fulfilled, (state, action) => {
        state.isEditingPlan = false;
        state.selectedPlan = action.payload;
      })
      .addCase(editPlanAsync.rejected, (state, action) => {
        state.isEditingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

// Export reducer
export default plansSlice.reducer;
